import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff } from 'lucide-react';
import { fetchWithAuth } from '../../utils/fetchWithAuth';
import { getErrorMessage } from '../../constants/errorCodes';
import { passwordIssue } from '../../utils/passwordUtils';
import PasswordRuleChecklist from '../../components/PasswordRuleChecklist';
import { applyLocale } from '../../utils/applyLocale';

function ResetPasswordPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const invite = searchParams.get('invite') === '1';
  const lang = searchParams.get('lang');

  // The invitation link names the user's language: nobody is signed in here to tell the page.
  useEffect(() => {
    if (lang) applyLocale(lang);
  }, [lang]);

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [shown, setShown] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    const issue = passwordIssue(password);
    if (issue) {
      setError(getErrorMessage(t, issue === 'too_long' ? 'PASSWORD_TOO_LONG' : 'RESET_PASSWORD_WEAK'));
      return;
    }

    if (password !== confirmPassword) {
      setError(t('The passwords do not match.'));
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetchWithAuth('auth/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password })
      });

      let data = {};
      try { data = await response.json(); } catch (_) {}

      if (!response.ok) {
        const code = data.errorCode || 'DEFAULT';
        setError(getErrorMessage(t, code));
        return;
      }

      setSuccess(true);
    } catch {
      setError(t('An error occurred. Please try again later.'));
    } finally {
      setIsLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="fixed inset-0 bg-gray-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full text-center">
          <h2 className="text-xl font-bold mb-2">{t('Invalid link')}</h2>
          <p className="text-gray-600 mb-4">{t('Invalid or expired link. Please request a new one.')}</p>
          <button
            onClick={() => navigate('/')}
            className="bg-black hover:bg-gray-800 text-white px-4 py-2 rounded-md"
          >
            {t('Back to sign in')}
          </button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="fixed inset-0 bg-gray-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full text-center">
          <h2 className="text-xl font-bold mb-2">{invite ? t('Password set') : t('Password reset')}</h2>
          <p className="text-gray-600 mb-4">
            {invite ? t('Your password is set. You can now sign in.') : t('Your password has been reset successfully.')}
          </p>
          <button
            onClick={() => navigate('/')}
            className="bg-black hover:bg-gray-800 text-white px-4 py-2 rounded-md"
          >
            {t('Sign in')}
          </button>
        </div>
      </div>
    );
  }

  // One state for both fields: revealing one reveals the other, so the two can be compared.
  const visibilityToggle = (
    <button
      type="button"
      onClick={() => setShown((value) => !value)}
      aria-label={shown ? t('Hide the password') : t('Show the password')}
      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
    >
      {shown ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
    </button>
  );

  return (
    <div className="fixed inset-0 bg-gray-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full">
        <h2 className="text-xl font-bold mb-2">{invite ? t('Welcome to GPS') : t('New password')}</h2>
        <p className="text-gray-600 mb-4">
          {invite ? t('Choose your password to activate your account.') : t('Enter your new password.')}
        </p>
        <form onSubmit={handleSubmit}>
          <div className="relative mb-1">
            <input
              type={shown ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t('New password')}
              className="w-full border rounded-md px-3 py-2 pr-10"
              disabled={isLoading}
              aria-describedby="password-rule"
              autoFocus
            />
            {visibilityToggle}
          </div>
          <div className="mb-3">
            <PasswordRuleChecklist id="password-rule" password={password} />
          </div>
          <div className="relative mb-3">
            <input
              type={shown ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder={t('Confirm the password')}
              className="w-full border rounded-md px-3 py-2 pr-10"
              disabled={isLoading}
            />
            {visibilityToggle}
          </div>

          {error && (
            <div className="text-red-600 text-sm mb-3">{error}</div>
          )}

          <button
            type="submit"
            className="bg-black hover:bg-gray-800 text-white px-4 py-2 rounded-md disabled:opacity-60"
            disabled={isLoading || !password || !confirmPassword}
          >
            {isLoading ? t('Updating…') : t('Validate')}
          </button>
        </form>
      </div>
    </div>
  );
}

export default ResetPasswordPage;
